import { Accordion, AccordionPanel, Col, FormGroup, Row, SelectableBox } from '@flos/react-ui';
import { addMinutes, differenceInMinutes, format, isBefore } from 'date-fns';
import _groupBy from 'lodash.groupby';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useResourceBundle } from '../contexts/ResourceContext';
import { navigationActions } from '../store/navigation.slice';
import { IState, Slots } from '../types';
import { ISO_TIME_FORMAT, mergeDateTime, TIME_FORMAT } from '../utils/date-utils';
import './TimeSlots.scss';

type OptionType = {
  label: string;
  value: string;
  disabled: boolean;
  data: Date;
};

const START_TIME = '08:00:00';
const FINISH_TIME = '18:00:00';

/**
 * Get a list of "default" time slots
 */
const getDefaultTimeSlots = (): Slots => {
  const start = mergeDateTime(new Date(1970, 0, 1), START_TIME);
  const finish = mergeDateTime(new Date(1970, 0, 1), FINISH_TIME);

  return [...new Array(differenceInMinutes(finish, start) / 15)]
    .map((_, idx) => ({
      [format(addMinutes(start, 15 * idx), ISO_TIME_FORMAT)]: 0,
    }))
    .reduce((value, current) => ({ ...value, ...current }), {});
};

const getTimeSlotExpandIndex = (
  selectedTime: string | undefined,
  categorisedOptions: { [key: string]: OptionType[] }
): string => {
  // Find by selectedTime
  if (selectedTime) {
    for (let [key, options] of Object.entries(categorisedOptions)) {
      if (options.find((option) => option.value === selectedTime)) {
        return key;
      }
    }
  } else {
    for (let [key, options] of Object.entries(categorisedOptions)) {
      if (options.some((option) => !option.disabled)) {
        return key;
      }
    }
  }

  return '';
};

const TimeSlots = ({ date, slots }: { date?: Date; slots?: Slots }) => {
  const selectedDate = useMemo(() => date ?? new Date('1970-01-01'), [date]);
  const slotsOfTheDay = slots ?? getDefaultTimeSlots();

  const dispatch = useDispatch();
  const resource = useResourceBundle();

  const options: OptionType[] = useMemo(
    () =>
      Object.entries(slotsOfTheDay).map(([timeStr, slotsLeft]) => {
        const time = mergeDateTime(selectedDate, timeStr);
        const formattedTime = format(time, TIME_FORMAT);
        return {
          label: formattedTime,
          value: formattedTime,
          disabled: date === undefined || slotsLeft <= 0,
          data: time,
        };
      }),
    [selectedDate, date, slotsOfTheDay]
  );

  const categorisedOptions: { [key: string]: OptionType[] } = useMemo(
    () =>
      _groupBy(options, (option) => {
        const time = option.data;

        if (isBefore(time, mergeDateTime(selectedDate, '11:00:00'))) {
          return 0;
        } else if (isBefore(time, mergeDateTime(selectedDate, '14:00:00'))) {
          return 1;
        } else {
          return 2;
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options]
  );

  const selectedTime = useSelector<IState, string | undefined>((state) => state.navigation.bookingTime);
  const activeIndex = getTimeSlotExpandIndex(selectedTime, categorisedOptions);

  const setSelectedTime = (time: string) => {
    dispatch(navigationActions.selectBookingTime(time));
  };

  const renderTimeSlots = timeSlots(selectedTime, setSelectedTime);

  // An array of (categorisedOptions.length) elements to store the selected time slot in the respective section
  const selectedTimeLabels = Object.values(categorisedOptions).map((optionTypes) =>
    optionTypes.some((option) => option.value === selectedTime)
      ? `${resource['label.schedule.select-time.header']} ${selectedTime}`
      : undefined
  );

  return (
    <div className="time-slots">
      <h4>{resource['label.schedule.select-time']}</h4>
      <FormGroup maxWidth="wide" data-testid="timeslot-component" id="timeslot-component">
        <Accordion activeIndex={activeIndex}>
          {categorisedOptions[0] ? (
            <AccordionPanel title={resource['label.schedule.timeslot.morning']} subtitle={selectedTimeLabels[0]}>
              <Row className="slots-group">{categorisedOptions[0].map(renderTimeSlots)}</Row>
            </AccordionPanel>
          ) : null}
          {categorisedOptions[1] ? (
            <AccordionPanel title={resource['label.schedule.timeslot.noon']} subtitle={selectedTimeLabels[1]}>
              <Row className="slots-group">{categorisedOptions[1].map(renderTimeSlots)}</Row>
            </AccordionPanel>
          ) : null}
          {categorisedOptions[2] ? (
            <AccordionPanel title={resource['label.schedule.timeslot.afternoon']} subtitle={selectedTimeLabels[2]}>
              <Row className="slots-group">{categorisedOptions[2].map(renderTimeSlots)}</Row>
            </AccordionPanel>
          ) : null}
        </Accordion>
      </FormGroup>
    </div>
  );
};

export default TimeSlots;

const timeSlots =
  (selectedTime: string | undefined, setSelectedTime: (time: string) => void) => (option: OptionType) => (
    <Col sm={4} xs={6} key={option.value}>
      <SelectableBox
        className="slot"
        dimmed
        headerText={option.label}
        active={option.value === selectedTime}
        onSelect={() => setSelectedTime(option.value)}
        disabled={option.disabled}
      />
    </Col>
  );
